// @flow

import i18n from 'i18next'

import { getBrandTheme } from '../../core/brandTheme'

export type CrashTexts = {
  title: string,
  subtitle: string,
  reload: string,
  support: string,
}

// Kept here rather than in the app locales: the crash screen must work
// even when i18n failed or was never initialised
export const CRASH_TEXTS: { [language: string]: CrashTexts } = {
  en: {
    title: 'Something went wrong',
    subtitle:
      'The application ran into an unexpected problem. Please reload it. If the problem persists, take a close-up photo of this screen so the QR code is clearly visible, and send it to support.',
    reload: 'Reload application',
    support: 'Support information',
  },
  es: {
    title: 'Algo salió mal',
    subtitle:
      'La aplicación encontró un problema inesperado. Vuelva a cargarla. Si el problema persiste, tome una foto de cerca de esta pantalla para que el código QR se vea con claridad y envíela a soporte.',
    reload: 'Recargar aplicación',
    support: 'Información para soporte',
  },
  pt: {
    title: 'Algo deu errado',
    subtitle:
      'O aplicativo encontrou um problema inesperado. Recarregue-o. Se o problema continuar, tire uma foto de perto desta tela, de forma que o código QR fique bem visível, e envie-a ao suporte.',
    reload: 'Recarregar aplicativo',
    support: 'Informações para o suporte',
  },
  ru: {
    title: 'Что-то пошло не так',
    subtitle:
      'В приложении произошла непредвиденная ошибка. Перезагрузите его. Если проблема повторяется, сфотографируйте этот экран вблизи, чтобы QR-код был хорошо виден, и отправьте фото в службу поддержки.',
    reload: 'Перезагрузить приложение',
    support: 'Информация для поддержки',
  },
  el: {
    title: 'Κάτι πήγε στραβά',
    subtitle:
      'Η εφαρμογή αντιμετώπισε ένα απρόσμενο πρόβλημα. Φορτώστε την ξανά. Αν το πρόβλημα παραμένει, φωτογραφίστε αυτή την οθόνη από κοντά, ώστε ο κωδικός QR να φαίνεται καθαρά, και στείλτε τη φωτογραφία στην υποστήριξη.',
    reload: 'Επαναφόρτωση εφαρμογής',
    support: 'Πληροφορίες για την υποστήριξη',
  },
  he: {
    title: 'משהו השתבש',
    subtitle:
      'באפליקציה אירעה בעיה בלתי צפויה. יש לטעון אותה מחדש. אם הבעיה נמשכת, צלמו את המסך הזה מקרוב כך שקוד ה-QR ייראה בבירור, ושלחו את התמונה לתמיכה.',
    reload: 'טעינה מחדש של האפליקציה',
    support: 'מידע לתמיכה',
  },
  fr: {
    title: "Une erreur s'est produite",
    subtitle:
      "L'application a rencontré un problème inattendu. Veuillez la recharger. Si le problème persiste, prenez une photo rapprochée de cet écran afin que le code QR soit bien lisible, puis envoyez-la au support.",
    reload: "Recharger l'application",
    support: 'Informations pour le support',
  },
  ro: {
    title: 'A apărut o problemă',
    subtitle:
      'Aplicația a întâmpinat o problemă neașteptată. Vă rugăm să o reîncărcați. Dacă problema persistă, fotografiați acest ecran de aproape, astfel încât codul QR să fie clar vizibil, și trimiteți fotografia echipei de asistență.',
    reload: 'Reîncărcați aplicația',
    support: 'Informații pentru asistență',
  },
}

const RTL_LANGUAGES = ['he']

export const DEFAULT_CRASH_TEXTS = { language: 'en', texts: CRASH_TEXTS.en, dir: 'ltr' }

// "pt-BR" → "pt", "es_ES" → "es", legacy "iw" → "he"; unsupported values → ''
// eslint-disable-next-line complexity
export const normalizeLanguage = (value: mixed): string => {
  if (typeof value !== 'string') return ''

  const base = value.trim().toLowerCase().split(/[-_]/)[0]
  const language = base === 'iw' ? 'he' : base

  return Object.prototype.hasOwnProperty.call(CRASH_TEXTS, language) ? language : ''
}

// Same order the app uses, without initialising i18n for the crash screen:
// current i18n language → saved setting → brand default → English
const LANGUAGE_SOURCES = [
  () => (i18n.isInitialized ? i18n.language : ''),
  () => window.localStorage.getItem('i18nextLng'),
  () => getBrandTheme().brandConfig.defaultLanguage,
]

// eslint-disable-next-line complexity
export const getCrashLanguage = (): string => {
  for (let i = 0; i < LANGUAGE_SOURCES.length; i += 1) {
    try {
      const language = normalizeLanguage(LANGUAGE_SOURCES[i]())

      if (language) return language
    } catch (e) {
      // Try the next source
    }
  }

  return 'en'
}

export const getCrashTexts = (): typeof DEFAULT_CRASH_TEXTS => {
  const language = getCrashLanguage()

  return {
    language,
    texts: CRASH_TEXTS[language],
    dir: RTL_LANGUAGES.includes(language) ? 'rtl' : 'ltr',
  }
}
